"use client";
import { useState } from "react";
import { formatDateInZone, formatCurrency, getCurrencySymbol } from "../../utils/formatters";
import { SalesTableProps } from "./SalesInterface";
import { ButtonsFilters } from "../filters/ButtonsFilters";
import { PaidState } from "../../types/order/paidState";
import { LogisticState } from "../../types/order/logisticState";
import {
  downloadOrders,
  getOrderDetails,
  deleteOrder,
} from "../../services/orders/orderAxios";
import { dictionary, Language } from "../../utils/dictionary";
import { useAuth } from "../../context/AuthContext";
import { DefaultButton } from "../buttons/DefaultButton";
import { PaymentProvider } from "../../pages/SalesPage/SalesPageInterface";
import { IconTrash } from "../../assets/icons/icons";
import { BaseModal } from "../modals/BaseModal";
import { handleApi } from "../../utils/handleApi";
import { Order } from "../../pages/SalesPage/SalesPageInterface";
import { toast } from "react-hot-toast";
import { DateFilter } from "./DateFilter";
import { ProductFilter } from "./ProductFilter";
import { Link } from "react-router-dom";

interface DeleteOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  message: string;
  onConfirm: () => void;
  loading: boolean;
  language: Language;
}

const DeleteOrderModal = ({
  isOpen,
  onClose,
  message,
  onConfirm,
  loading,
  language,
}: DeleteOrderModalProps) => (
  <BaseModal
    isOpen={isOpen}
    onClose={onClose}
    imageSrc="https://fluxi.s3.amazonaws.com/prod/assets/media/warning-purple.gif"
    imageAlt="Warning"
    title={dictionary["delete-order"][language]}
    description={message}
    buttonOne={{
      text: dictionary["delete"][language],
      onClick: onConfirm,
      loading,
      disabled: loading,
    }}
    buttonTwo={{
      text: dictionary["cancel"][language],
      onClick: onClose,
    }}
  />
);

const EmptyState = ({ language }: { language: Language }) => (
  <tr>
    <td colSpan={8} className="px-6 py-12 text-center">
      <div className="flex flex-col items-center justify-center">
        <svg
          className="w-16 h-16 text-gray-300 mb-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
        <h3 className="text-lg font-medium text-gray-900 mb-1">
          {dictionary["no-orders-found"]?.[language] ||
            "No se encontraron órdenes"}
        </h3>
        <p className="text-sm text-gray-500">
          {dictionary["try-different-filters"]?.[language] ||
            "Intenta con diferentes filtros o rango de fechas"}
        </p>
      </div>
    </td>
  </tr>
);

interface OrderRowProps {
  order: Order;
  currencySymbol: string;
  language: Language;
  timezone: string;
  paymentProviderLabels: Record<PaymentProvider, string>;
  onDelete: (order: Order) => void;
}

const IntegrationErrorIcon = ({ language }: { language: Language }) => (
  <div className="relative group flex justify-center">
    <div className="bg-yellow-500 rounded-lg p-2 cursor-help">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="lucide lucide-alert-triangle text-white"
      >
        <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"></path>
        <path d="M12 9v4"></path>
        <path d="M12 17h.01"></path>
      </svg>
    </div>
    <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 w-72 bg-gray-900 text-white text-xs rounded-lg p-3 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 shadow-lg">
      <p className="font-medium mb-1">
        {dictionary["sync-error-title"]?.[language] || "Error de sincronización"}
      </p>
      <p className="text-gray-300">
        {dictionary["sync-error-description"]?.[language] || "Error al conectar con el API de inventario"}
      </p>
    </div>
  </div>
);

const OrderRow = ({
  order,
  currencySymbol,
  language,
  timezone,
  paymentProviderLabels,
  onDelete,
}: OrderRowProps) => {
  const hasIntegrationError = order.dropi_integration_error || order.shopify_integration_error;

  return (
    <tr key={order.id} className="hover:bg-gray-50">
      <td className="px-4 py-4">
        {hasIntegrationError && <IntegrationErrorIcon language={language} />}
      </td>
      <td className="px-4 py-4 text-sm text-gray-500 truncate">
        {formatDateInZone(order.created_at, timezone)}
      </td>
      <td
        className="px-4 py-4 text-sm text-blue-500 truncate overflow-hidden"
        title={`${order.customer_name} ${order.customer_las_name}`}
      >
        {`${order.customer_name || ""} ${order.customer_las_name || ""}`}
      </td>
      <td className="px-4 py-4 text-sm text-gray-900 truncate">
        {formatCurrency(Number(order.total_value_with_discount || 0), currencySymbol)}
      </td>
      <td className="px-4 py-4 text-sm text-gray-900">
        {order.payment_state === PaidState.PAID ? (
          <span className="inline-flex items-center px-3 py-1 text-xs rounded-full bg-green-100 text-green-800 max-w-full">
            <span className="w-2 h-2 mr-1.5 bg-green-500 rounded-full flex-shrink-0"></span>
            <span className="truncate">{dictionary["paid-state"][language]}</span>
          </span>
        ) : (
          <span className="inline-flex items-center px-3 py-1 text-xs rounded-full bg-red-100 text-red-800 max-w-full">
            <span className="w-2 h-2 mr-1.5 bg-red-500 rounded-full flex-shrink-0"></span>
            <span className="truncate">
              {order.payment_state === "pending"
                ? dictionary["pending-state"][language]
                : dictionary["unpaid-state"][language]}
            </span>
          </span>
        )}
      </td>
      <td className="px-4 py-4 text-sm text-gray-900">
        <span className="inline-flex items-center px-3 py-1 text-xs rounded-full bg-[#5050EA]/15 text-[#5050EA] max-w-full">
          <span className="w-2 h-2 mr-1.5 bg-[#5050EA] rounded-full flex-shrink-0"></span>
          <span className="truncate">
            {(order.payment_provider &&
              paymentProviderLabels[order.payment_provider]) ||
              dictionary["counter-delivery"][language]}
          </span>
        </span>
      </td>
      <td className="px-4 py-4 text-sm text-gray-900">
        {order.logistic_state === LogisticState.IN_PROGRESS ? (
          <span className="inline-flex items-center px-3 py-1 text-xs rounded-full bg-blue-100 text-blue-800 max-w-full">
            <span className="truncate">{dictionary["fulfilled-state"][language]}</span>
          </span>
        ) : (
          <span className="inline-flex items-center px-3 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800 max-w-full">
            <span className="truncate">{dictionary["unfulfilled-state"][language]}</span>
          </span>
        )}
      </td>
      <td className="px-4 py-4 text-sm text-gray-500">
        <div className="flex items-center gap-3">
          <Link
            to={`/order/${order.hash_id}${location.search}`}
            className="text-blue-500 whitespace-nowrap text-xs"
          >
            {dictionary["view-details"]?.[language] || "Ver detalles"}
          </Link>
          <button onClick={() => onDelete(order)} className="flex-shrink-0">
            <IconTrash />
          </button>
        </div>
      </td>
  </tr>
  );
};

const SalesTable = ({
  filterOrders,
  orders,
  activeFilter,
  onOrderDeleted,
  onFilterByDate,
  activeDateFilter,
  onRemoveDateFilter,
  products,
  onFilterByProductId,
  activeProductFilters,
  onRemoveProductFilter,
}: SalesTableProps) => {
  const { language, country, timezone } = useAuth();
  const currencySymbol = getCurrencySymbol(country);
  const [isDownloading, setIsDownloading] = useState(false);
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState<Order | null>(null);
  const [isLoadingOrderDetails, setIsLoadingOrderDetails] = useState(false);
  const [deleteOrderMessage, setDeleteOrderMessage] = useState(
    dictionary["are-you-sure-you-want-to-delete-this-order"][language]
  );

  const paymentProviderLabels = {
    [PaymentProvider.CONTRA_ENTREGA]: "Contra Entrega",
    [PaymentProvider.MERCADO_PAGO]: "Mercado Pago",
    [PaymentProvider.STRIPE]: "Stripe",
  };

  const handleDownloadOrders = async () => {
    setIsDownloading(true);
    const ordersIds = { order_ids: orders?.map((order: Order) => order.id) ?? [] };
    const ordersCvs = await downloadOrders(ordersIds);
    window.open(ordersCvs.s3_url, "_blank");
    setIsDownloading(false);
  };

  const handleDeleteOrder = async (order: Order) => {
    setOrderToDelete(order);
    setOpenModalDelete(true);
    setIsLoadingOrderDetails(true);
    const { res, error } = await handleApi(getOrderDetails(order.hash_id));
    if (error) {
      setDeleteOrderMessage(
        dictionary["error-getting-order-details"][language]
      );
      setIsLoadingOrderDetails(false);
      return;
    }

    const hasDropiIntegration = res?.order?.dropi_state;
    const hasShopifyIntegration = res?.order?.shopify_integration_state;
    
    let message = dictionary["are-you-sure-you-want-to-delete-this-order"][language];

    if (hasDropiIntegration && hasShopifyIntegration) {
      message = dictionary["this-order-is-integrated-with-dropi-and-shopify"][language];
    } else if (hasDropiIntegration) {
      message = dictionary["this-order-is-integrated-with-dropi"][language];
    } else if (hasShopifyIntegration) {
      message = dictionary["this-order-is-integrated-with-shopify"][language];
    }

    setDeleteOrderMessage(message);
    setIsLoadingOrderDetails(false);
  };

  const confirmDeleteOrder = async () => {
    if (!orderToDelete) return;
    setIsLoadingOrderDetails(true);
    const { error } = await handleApi(deleteOrder(orderToDelete.id));
    if (error) {
      toast.error(dictionary["error-deleting-order"][language]);
      setIsLoadingOrderDetails(false);
      return;
    }
    toast.success(dictionary["order-deleted"][language]);
    onOrderDeleted(orderToDelete.id.toString());
    setIsLoadingOrderDetails(false);
    setOpenModalDelete(false);
  };

  return (
    <div className="bg-white rounded-xl shadow-sm">
      <DeleteOrderModal
        isOpen={openModalDelete}
        onClose={() => setOpenModalDelete(false)}
        message={deleteOrderMessage}
        onConfirm={confirmDeleteOrder}
        loading={isLoadingOrderDetails}
        language={language}
      />

      {/* filtros y botones */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 px-4 pt-4">
        <ButtonsFilters
          filterOrders={filterOrders}
          activeFilter={activeFilter}
        />
        <DefaultButton
          title={
            isDownloading
              ? dictionary["downloading"][language]
              : dictionary["download-orders"][language]
          }
          onClick={handleDownloadOrders}
          isLoading={isDownloading}
        />
      </div>

      {/* filtro por fecha y producto */}
      <div className="mt-4 flex flex-wrap gap-4 px-4">
        <DateFilter onFilterApply={onFilterByDate} />
        <ProductFilter
          products={products}
          onProductSelect={onFilterByProductId}
        />
      </div>

      {/* etiquetas de filtros activos */}
      {(activeDateFilter || activeProductFilters.length > 0) && (
        <div className="mt-4 flex flex-wrap gap-2 px-4">
          {/* Filtro de fecha */}
          {activeDateFilter && (
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#E8EAFF] text-[#4D5FE8] rounded-full text-sm font-medium">
              <span>{activeDateFilter.label}</span>
              <button
                onClick={onRemoveDateFilter}
                className="hover:bg-[#4D5FE8] hover:text-white rounded-full p-0.5 transition-colors"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          )}

          {/* Filtros de productos */}
          {activeProductFilters.map((product: { id: string; name: string }) => (
            <div
              key={product.id}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#E2FBE8] text-[#3C7E45] rounded-full text-sm font-medium max-w-[250px]"
            >
              <span className="truncate">{product.name}</span>
              <button
                onClick={() => onRemoveProductFilter(product.id)}
                className="hover:bg-[#3C7E45] hover:text-white rounded-full p-0.5 transition-colors"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}

      {/* tabla */}
      <div className="overflow-x-auto mt-4" style={{ minWidth: 0 }}>
        <table className="w-full table-fixed" style={{ minWidth: "900px" }}>
          <colgroup>
            <col style={{ width: "40px" }} />
            <col style={{ width: "13%" }} />
            <col style={{ width: "17%" }} />
            <col style={{ width: "12%" }} />
            <col style={{ width: "14%" }} />
            <col style={{ width: "15%" }} />
            <col style={{ width: "14%" }} />
            <col style={{ width: "15%" }} />
          </colgroup>
          <thead className="bg-gray-50">
            <tr>
              {[
                "","date-and-time","customer","total","status","payment-method","fulfillment",""
              ].map((key, i) => (
                <th
                  key={i}
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider truncate"
                >
                  {dictionary[key]?.[language] || ""}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {orders?.length === 0 ? (
              <EmptyState language={language} />
            ) : (
              orders.map((order) => (
                <OrderRow
                  key={order.id}
                  order={order}
                  currencySymbol={currencySymbol}
                  language={language}
                  timezone={timezone}
                  paymentProviderLabels={paymentProviderLabels}
                  onDelete={handleDeleteOrder}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SalesTable;
